/**
 * Compiled schedule artifacts and evaluation context for the shared
 * ScheduleEngine (docs/plans/planned-task-schedule.md §2).
 *
 * Everything here is pure data: local YYYY-MM-DD date strings, no Date
 * instants, no persistence concerns.
 */

import { ScheduleWeekday } from './schedule.rule';

/**
 * Derived density class: set ⇒ class-evaluated, never materialised into DueDay.include.
 *
 * 'daily' | 'weekdays' | 'sixDay' are FIXED patterns — 'weekdays' means Mon–Fri on every tenant,
 * whatever the work week says. 'workingDays' is the one DYNAMIC class: its day set is
 * `ctx.workWeek`, resolved per line at query time, so no static RRULE can express it and the
 * board has to count its applicable days per line rather than from a fixed `$switch`.
 */
export type ScheduleClass = 'daily' | 'weekdays' | 'sixDay' | 'workingDays';

/** Output of ScheduleEngine.compile(). Regenerable from the editor model at any time. */
export interface CompiledTaskSchedule {
    /** iCal RRULE. DTSTART = ctx.effectiveFrom (local). WKST=MO. Absent for perVisit / afterService. */
    scheduleRule?: string;
    /** RDATE set, sorted ascending, deduped. YYYY-MM-DD. */
    extraDates?: string[];
    /** EXDATE set (expanded blackout ranges + single skips), sorted ascending, deduped. YYYY-MM-DD. */
    exdates?: string[];
    /** Set only when the schedule qualifies for class evaluation (see plan §2 whitelist). */
    scheduleClass?: ScheduleClass;
}

/** A single skipped date or an inclusive blackout range. */
export interface ScheduleSkip {
    date?: string;
    from?: string;
    to?: string;
    reason?: string;
}

export interface ScheduleExtraDate {
    date: string;
    note?: string;
}

/** Task-level exceptions plus inherited plan/line blackout masks (plan §3.8 Exception levels). */
export interface ScheduleExceptions {
    skips?: ScheduleSkip[];
    extras?: ScheduleExtraDate[];
    /** Inherited read-only masks — union-applied after task-level exceptions. */
    lineBlackouts?: ScheduleSkip[];
    planBlackouts?: ScheduleSkip[];
}

/**
 * Evaluation context. Always injected — the engine never reads clocks,
 * settings, or repositories.
 */
export interface ScheduleContext {
    /** Line-local anchor date, YYYY-MM-DD. DTSTART for calendar rules; default firstDue for afterService. */
    effectiveFrom: string;
    /** Inclusive contract boundary. No occurrence may survive after this local date. */
    effectiveTo?: string;
    /** IANA timezone of the line. Only used to interpret 'today'; all outputs stay local date strings. */
    timezone: string;
    /**
     * WBS-11102 — the site the line's dues belong to: the parent plan's `site._id`, or `DUE_DAY_NO_SITE`.
     *
     * The engine never reads it; it rides on the context because the context is the ONE thing already
     * resolved per line by every projection caller, and the site is a property of the line's plan. It
     * is what keys the due-day index, so resolving it anywhere else would mean a second plan read per
     * task on the horizon tick. OPTIONAL because the web schedule previews (editor, outline tooltip,
     * schedule card) build a context to evaluate dates and never project; the projection service
     * substitutes `DUE_DAY_NO_SITE` when it is absent.
     */
    siteId?: string;
    /** Working weekdays, 1 = Monday … 7 = Sunday. From tenant settings. */
    workWeek: ScheduleWeekday[];
    /** Resolved regional public-holiday dates, YYYY-MM-DD. Always part of "non-working" when present. */
    holidays?: string[];
    /** afterService only: local date of the last completed service for this task. */
    lastCompleted?: string;
    /**
     * afterService with `anchor: 'anniversary'` only: the date the last completed service was DUE, as
     * opposed to the date it was performed.
     *
     * An anniversary regime measures the next cycle from the contract date, so servicing early to dodge
     * a holiday period does not permanently drag the schedule forward. Without this the engine cannot
     * tell an early service from a late one and falls back to completion anchoring.
     */
    lastScheduledDue?: string;
    /** Injected current local date, YYYY-MM-DD. Never new Date() inside the engine. */
    today: string;
}

/**
 * Mon–Fri. The work week every empty or unusable resolution falls back to.
 *
 * The twin of the API's `DEFAULT_WORK_WEEK` in the schedule context, declared here because the
 * ENGINE needs it: an empty week reaching evaluation has to be healed at the boundary, and the
 * engine cannot import from the API side.
 */
export const FALLBACK_WORK_WEEK: ScheduleWeekday[] = [1, 2, 3, 4, 5];

/**
 * The work week to EVALUATE against: an empty array means the same thing as an absent one.
 *
 * WBS-11017. `workWeek` is resolved down a chain of `?? default` fallbacks, and `??` only fires on
 * null/undefined — so a plan or line that somehow holds `workingDays: []` (a legacy row, a partial
 * import, a write that predates the guard) resolves to an empty week, and an empty week makes every
 * working-day task due on zero days. Silently. No visits, no jobs, no error.
 *
 * "No working days at all" is never a thing a contract means; it is what a missing value looks like
 * when the fallback tested the wrong condition. So absent and empty are treated identically here,
 * and this is the single place that decides it. Guards still REJECT an empty array on write — this
 * is the evaluation-time backstop for the rows already stored, not a licence to store one.
 */
export function evaluableWorkWeek(workWeek?: readonly ScheduleWeekday[] | null): ScheduleWeekday[] {

    return workWeek?.length ? [...workWeek] : [...FALLBACK_WORK_WEEK];
}

/** A context safe to evaluate: same context, with an empty work week healed to the fallback. */
export function evaluableScheduleContext(context: ScheduleContext): ScheduleContext {

    return context.workWeek?.length ? context : { ...context, workWeek: evaluableWorkWeek(context.workWeek) };
}

export type ScheduleChange = { type: 'skipDate'; date: string; reason?: string } | { type: 'skipRange'; from: string; to: string; reason?: string } | { type: 'extraDate'; date: string; note?: string } | { type: 'moveDate'; from: string; to: string };
